import { ChartTable } from '../../components/charts/ChartTable.tsx';
import { InfoPopover } from '../../components/InfoPopover.tsx';
import { formatNumber, formatPercent } from '../../lib/format.ts';
import { share } from '../../lib/home.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';

export interface HomeFunnelData {
  size: number;
  contacted: number;
  won: number;
  lost: number;
  open: number;
}

/** Outcome colours carry meaning (bought / not buying / undecided) and always come with a label. */
export const OUTCOME_COLORS = {
  won: 'var(--outcome-won)',
  open: 'var(--outcome-open)',
  lost: 'var(--outcome-lost)',
} as const;

/**
 * The home funnel, as management knows it from the old report — Lid → Aloqaga chiqildi — but the
 * last step is drawn as what it is: the contacted leads split into three outcomes. "Not buying"
 * sits beside "bought", not below it, and the three parts add up to the contacted bar.
 */
export function HomeFunnel({ data }: { data: HomeFunnelData }) {
  const t = useT();
  const locale = useLocale();
  const f = t.home.funnel;
  const max = Math.max(1, data.size);
  const width = (count: number) => `${Math.max(count > 0 ? 1.5 : 0, (count / max) * 100)}%`;
  const notContacted = data.size - data.contacted;
  const outcomes = [
    { key: 'won', label: f.won, count: data.won },
    { key: 'open', label: f.open, count: data.open },
    { key: 'lost', label: f.lost, count: data.lost },
  ] as const;

  return (
    <figure aria-label={f.title} className="space-y-4">
      <div className="space-y-2" aria-hidden="true" data-testid="home-funnel">
        <FunnelRow label={f.lid} count={data.size} width={width(data.size)} locale={locale}>
          <div className="h-9 rounded-md bg-primary" />
        </FunnelRow>
        <p className="text-center text-xs text-muted-foreground">
          ↓{' '}
          {f.notContacted(
            formatNumber(notContacted, locale),
            formatPercent(share(notContacted, data.size), locale),
          )}
        </p>
        <FunnelRow
          label={f.contacted}
          count={data.contacted}
          width={width(data.contacted)}
          locale={locale}
          rate={formatPercent(share(data.contacted, data.size), locale)}
        >
          <div className="h-9 rounded-md bg-primary/85" />
        </FunnelRow>
        <FunnelRow label="" count={null} width={width(data.contacted)} locale={locale}>
          <div className="flex h-9 gap-0.5 overflow-hidden rounded-md">
            {outcomes.map((o) =>
              o.count > 0 ? (
                <div
                  key={o.key}
                  style={{ flexGrow: o.count, background: OUTCOME_COLORS[o.key] }}
                  className="min-w-1"
                />
              ) : null,
            )}
          </div>
        </FunnelRow>
      </div>

      <ul className="grid gap-2 sm:grid-cols-3" aria-label={f.title}>
        {outcomes.map((o) => (
          <li key={o.key} className="rounded-md border px-3 py-2">
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span
                aria-hidden="true"
                className="size-2.5 rounded-full"
                style={{ background: OUTCOME_COLORS[o.key] }}
              />
              {o.label}
            </p>
            <p className="text-xl font-semibold tabular-nums">{formatNumber(o.count, locale)}</p>
            <p className="text-xs text-muted-foreground">
              {formatPercent(share(o.count, data.contacted), locale)} {f.ofContacted}
            </p>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between gap-2 border-t pt-3 text-sm">
        <p className="font-medium">
          {f.conversion(formatPercent(share(data.won, data.size), locale))}
        </p>
        <InfoPopover label={f.title}>
          <p>{f.help}</p>
        </InfoPopover>
      </div>

      <ChartTable
        title={f.title}
        columns={['', t.common.total, '%']}
        rows={[
          [f.lid, formatNumber(data.size, locale), formatPercent(1, locale)],
          [
            f.contacted,
            formatNumber(data.contacted, locale),
            formatPercent(share(data.contacted, data.size), locale),
          ],
          ...outcomes.map((o) => [
            o.label,
            formatNumber(o.count, locale),
            formatPercent(share(o.count, data.size), locale),
          ]),
        ]}
      />
    </figure>
  );
}

function FunnelRow({
  label,
  count,
  width,
  rate,
  locale,
  children,
}: {
  label: string;
  count: number | null;
  width: string;
  rate?: string;
  locale: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,7.5rem)_1fr_minmax(0,5.5rem)] items-center gap-3 text-sm">
      <span className="truncate text-muted-foreground">{label}</span>
      <div className="flex justify-center">
        <div style={{ width }}>{children}</div>
      </div>
      <span className="text-right">
        {count !== null ? (
          <span className="font-semibold tabular-nums">{formatNumber(count, locale)}</span>
        ) : null}
        {rate ? <span className="block text-xs text-muted-foreground">{rate}</span> : null}
      </span>
    </div>
  );
}
